import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { ApiVersionService, AuthService, HttpCancelService, LoadingService } from '@app/core/services';
import { environment } from '@environments/environment';

@Injectable()
export class SchoolLinqApiInterceptor implements HttpInterceptor {
	constructor(
		private readonly apiVersionService: ApiVersionService,
		private readonly authService: AuthService,
		private readonly httpCancelService: HttpCancelService,
		private readonly loadingService: LoadingService
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		let requestUrl = req.url;

		// Don't modify the request URL for OIDC authority requests, otherwise they will fail.
		if (requestUrl.startsWith(environment.OIDC_AUTHORITY)) {
			this.loadingService.show();
			return next.handle(req).pipe(finalize(() => this.loadingService.hide()));
		}

		// Only prepend API_URL for relative URLs (our API), not absolute URLs (OIDC endpoints, external APIs)
		if ((requestUrl.indexOf('api/') >= 0 || requestUrl.indexOf('token') >= 0) && !requestUrl.startsWith('http')) {
			requestUrl = environment.API_URL + requestUrl;
		}

		let authorization = '';
		// For Cognito, we likely will have a similar situation where we already have an access token that
		// we need to use, in which case we'll probably need to do something similar to the following:
		// if (this.authService.isAuth0TokenRequest(req)) {
		// 	// The token request following an Auth0 login has an Auth0 access token attached to it already,
		// 	// which we need to use as-is.
		// 	authorization = req.headers.get('Authorization');
		// } else {
		const authToken = this.authService.accessToken;
		if (authToken) {
			authorization = 'Bearer ' + authToken;
		}

		const httpsReq = req.clone({
			url: requestUrl,
			setHeaders: {
				Authorization: authorization,
			},
		});

		const isSkippedRequest = this.loadingService.isSkippedRequest(req);
		if (isSkippedRequest) {
			this.loadingService.skipNextRequest();
		}

		this.loadingService.show();

		return next.handle(httpsReq).pipe(
			takeUntil(this.httpCancelService.onCancelPendingRequests(req.url)),
			tap((res) => {
				if (res instanceof HttpResponse) {
					const responseApiVersion = res.headers.get('x-api-version');
					this.apiVersionService.checkApiVersion(responseApiVersion);
				}
			}),
			catchError((error: HttpErrorResponse) => {
				if (error.status === 401) {
					this.authService.logOut();
				}
				return throwError(() => error);
			}),
			finalize(() => {
				this.loadingService.hide();
			})
		);
	}
}
